import { BulkFulfillmentPlanTypeEnum } from '../../../../enums/bulk-fulfillment-plan-type';
import Global from '../../../../store/global-selectors';

export const BULK_UPDATE_SERVICE_ID = 'bulk-update-service';

const DRAWER_JOB_DATA_PROPERTY = 'jobData';

// TODO: Refactor useCapabilityContext to call capability-specific hooks instead of plain
// getAdditionalContext functions. This would allow reusing existing hooks (e.g.,
// useGetSellerProviderName) instead of duplicating logic in the helpers below.

/**
 * Filter services based on whether selected orders are international.
 * Mirrors the logic in shipping-service.js getInternationalFilter.
 * @param {Array} services - All extended services
 * @param {string} bulkFulfillmentPlanType - 'Domestic' | 'International' | 'Mixed'
 * @returns {Array} Filtered services
 */
const filterServicesByFulfillmentPlanType = (services, bulkFulfillmentPlanType) => {
  if (
    bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Domestic ||
    bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Mixed ||
    !bulkFulfillmentPlanType
  ) {
    return services;
  }

  return services.filter((service) => service.features?.supportsInternationalShipping === true);
};

/**
 * Build a display name for a seller label provider (account).
 * Mirrors the logic in use-get-seller-provider-name.js.
 * @param {object} sellerLabelProvider - The seller's label provider account
 * @param {Array} allSellerLabelProviders - All seller label providers (to check for multiples)
 * @param {Array} labelProviders - Reference data for all carriers
 * @returns {string} Display name for the provider account
 */
const getProviderAccountName = (sellerLabelProvider, allSellerLabelProviders, labelProviders) => {
  if (!sellerLabelProvider) {
    return '';
  }

  const matchingLabelProvider = labelProviders.find((p) => p.labelProviderId === sellerLabelProvider.labelProviderId);
  const carrierName = matchingLabelProvider?.name ?? '';

  if (sellerLabelProvider.useHouseCredentials) {
    return carrierName;
  }

  if (sellerLabelProvider.name) {
    return `${carrierName} - ${sellerLabelProvider.name}`;
  }

  const hasMultipleAccounts = allSellerLabelProviders.filter((p) => p.labelProviderId === sellerLabelProvider.labelProviderId).length > 1;

  if (hasMultipleAccounts && sellerLabelProvider.accountNumber) {
    return `${carrierName} (${sellerLabelProvider.accountNumber})`;
  }

  return carrierName;
};

export const bulkUpdateServiceCapability = {
  id: BULK_UPDATE_SERVICE_ID,
  getAdditionalContext: (reduxState) => {
    const extendedServices = Global.selectSellerExtendedServices(reduxState);
    const selectedOrderCount = Global.selectSelectedOrdersCount(reduxState);
    const drawerJobData = Global.selectBulkUpdateDrawerBulkUpdateProperty(reduxState, { property: DRAWER_JOB_DATA_PROPERTY });
    const bulkFulfillmentPlanType = drawerJobData?.bulkFulfillmentPlanType;

    const sellerLabelProviders = Global.selectSellerLabelProviders(reduxState);
    const labelProviders = Global.selectReferenceDatagetProviders(reduxState);

    const filteredServices = filterServicesByFulfillmentPlanType(extendedServices, bulkFulfillmentPlanType);

    const availableServices = filteredServices.map((service) => {
      const sellerLabelProvider = sellerLabelProviders.find((p) => p.sellerLabelProviderId === service.sellerLabelProviderId);
      const providerAccountName = getProviderAccountName(sellerLabelProvider, sellerLabelProviders, labelProviders);

      return {
        serviceId: service.serviceId,
        serviceName: service.name,
        sellerLabelProviderId: service.sellerLabelProviderId,
        providerAccountName,
      };
    });

    return {
      availableServices,
      selectedOrderCount,
      bulkFulfillmentPlanType: bulkFulfillmentPlanType ?? 'Unknown',
    };
  },
};
